import type { ComponentType } from "react"

import type { Palette } from "@/components/marketplace/palette"
import {
  BabyBottleIcon,
  BackpackIcon,
  CarIcon,
  CartIcon,
  DumbbellIcon,
  GamepadIcon,
  GiftIcon,
  GridIcon,
  HomeIcon,
  type IconProps,
  MonitorIcon,
  ShirtIcon,
  ShoeIcon,
  SparkleIcon,
  TagIcon,
  TruckFastIcon,
} from "@/components/ui"

interface CategoryVisual {
  palette?: Palette
  Icon: ComponentType<IconProps>
  image: string
}

interface CategoryRule extends Omit<CategoryVisual, "image"> {
  pattern: RegExp
  image?: string
}

// One place that turns a department handle into its color world, glyph and editorial
// photograph, so the drawer, the mega, the search overlay and the feed all read the
// same way. First match wins; handles are slug-ish (e.g. "electronica-audio-y-video",
// "descuentos"), so we test substrings. Unmatched handles stay brand-blue with the
// neutral grid.
//
// Every department resolves to a photograph, because the surfaces that use one have
// nothing to show without it. Handles that match no rule fall back to an assortment —
// a bit of everything, which is the only honest thing to show for "Otros".
const CATEGORY_RULES: ReadonlyArray<CategoryRule> = [
  { pattern: /descuent|oferta|outlet|liquidac|remate|nelofert/, palette: "ember", Icon: TagIcon, image: "/images/editorial/nb-ofertas-piso.jpg" },
  { pattern: /perfum|fraganc/, palette: "gold", Icon: SparkleIcon, image: "/images/editorial/band-perfumes.jpg" },
  { pattern: /belleza|cuidado|maquillaje|skincare|cosmet/, palette: "pink", Icon: SparkleIcon, image: "/images/editorial/band-belleza.jpg" },
  { pattern: /gaming|videojueg|consola/, palette: "ocean", Icon: GamepadIcon, image: "/images/editorial/nb-col-tecno.jpg" },
  { pattern: /electron|audio|video|tecnolog|computo|celular|monitor|pantalla/, palette: "ocean", Icon: MonitorIcon, image: "/images/editorial/band-electronica.jpg" },
  { pattern: /calzado|zapat|tenis|sneaker/, palette: "slate", Icon: ShoeIcon, image: "/images/editorial/band-calzado.jpg" },
  { pattern: /moda|ropa|vestir|fashion|textil/, palette: "pink", Icon: ShirtIcon, image: "/images/editorial/band-moda.jpg" },
  { pattern: /hogar|casa|cocina|jardin|mueble|decorac|electrodom/, palette: "gold", Icon: HomeIcon, image: "/images/editorial/band-hogar.jpg" },
  { pattern: /deport|aire.?libre|fitness|gym/, palette: "ocean", Icon: DumbbellIcon, image: "/images/editorial/band-deportes.jpg" },
  { pattern: /mochila|viaje|equipaje|maleta/, palette: "slate", Icon: BackpackIcon, image: "/images/editorial/nb-col-maleta.jpg" },
  { pattern: /vehiculo|auto|carro|moto|llanta|refacc/, palette: "slate", Icon: CarIcon, image: "/images/editorial/band-vehiculo.jpg" },
  { pattern: /bebe|infant|niñ/, palette: "ember", Icon: BabyBottleIcon, image: "/images/editorial/band-bebes.jpg" },
  { pattern: /juguete|juego/, palette: "ember", Icon: GiftIcon, image: "/images/editorial/band-juguetes.jpg" },
  { pattern: /manualidad|papeler|escolar/, palette: "ocean", Icon: SparkleIcon, image: "/images/editorial/rd-cuatro-papel.jpg" },
  { pattern: /abarrot|abarot|comida|bebida|aliment|limpieza|super|despensa/, palette: "gold", Icon: CartIcon, image: "/images/editorial/nb-col-cocina.jpg" },
  { pattern: /multimedia|entretenimiento|pelicula|musica|libro/, palette: "slate", Icon: GamepadIcon, image: "/images/editorial/collections/entretenimiento.jpg" },
  { pattern: /entrega|expres|envio|paqueter/, palette: "ocean", Icon: TruckFastIcon, image: "/images/editorial/of-caja.jpg" },
  { pattern: /primavera|verano|temporada|navidad|estacion/, palette: "gold", Icon: SparkleIcon, image: "/images/editorial/nb-col-jardin.jpg" },
]

const FALLBACK_IMAGE = "/images/editorial/nl-col-menos.jpg"

export const photoFromMetadata = (metadata: Record<string, unknown> | null | undefined): string | undefined => {
  const photo = metadata?.photo
  return typeof photo === "string" && photo !== "" ? photo : undefined
}

export const categoryVisual = (handle: string, photo?: string): CategoryVisual => {
  const match = CATEGORY_RULES.find(({ pattern }) => pattern.test(handle))
  return { palette: match?.palette, Icon: match?.Icon ?? GridIcon, image: photo ?? match?.image ?? FALLBACK_IMAGE }
}

export const paletteForCategoryHandle = (handle: string): Palette | undefined => categoryVisual(handle).palette
